import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPublicTenderForm, submitPublicQuote, declinePublicTender } from "../../services/interiorTender";

/**
 * Public, token-gated vendor quote page (no login). Reached from the magic link emailed on tender send.
 * The vendor sees the BV-specified SKU/brand per line and enters unit price, lead time and availability.
 */
const AVAILABILITY = [
  { value: "IN_STOCK", label: "In stock" },
  { value: "LIMITED", label: "Limited" },
  { value: "MADE_TO_ORDER", label: "Made to order" },
  { value: "UNAVAILABLE", label: "Unavailable" },
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

const TenderQuotePage = () => {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState({}); // boqId -> { unitPrice, leadTimeDays, availability, remarks }
  const [overallNotes, setOverallNotes] = useState("");
  const [serviceTotal, setServiceTotal] = useState(""); // SERVICE (move-in): single total price
  const [serviceLeadTime, setServiceLeadTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // "submitted" | "declined"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicTenderForm(token);
      setForm(res);
      const seed = {};
      const existingLines = res?.existingQuote?.lines || [];
      (res?.lines || []).forEach((l) => {
        const ex = existingLines.find((e) => e.interiorDesignerBoqId === l.interiorDesignerBoqId);
        seed[l.interiorDesignerBoqId] = {
          unitPrice: ex?.unitPrice ?? "",
          leadTimeDays: ex?.leadTimeDays ?? "",
          availability: ex?.availability ?? "",
          remarks: ex?.remarks ?? "",
        };
      });
      setRows(seed);
      setOverallNotes(res?.existingQuote?.overallNotes || "");
      setServiceTotal(res?.existingQuote?.totalAmount != null ? String(res.existingQuote.totalAmount) : "");
    } catch (e) {
      setError(e?.response?.data?.detail || "This tender link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const setRow = (boqId, field, value) =>
    setRows((prev) => ({ ...prev, [boqId]: { ...prev[boqId], [field]: value } }));

  const submit = async () => {
    const lines = (form.lines || []).map((l) => {
      const r = rows[l.interiorDesignerBoqId] || {};
      return {
        interiorDesignerBoqId: l.interiorDesignerBoqId,
        unitPrice: r.unitPrice === "" ? null : Number(r.unitPrice),
        leadTimeDays: r.leadTimeDays === "" ? null : Number(r.leadTimeDays),
        availability: r.availability || null,
        remarks: r.remarks || null,
      };
    });
    if (lines.some((l) => l.unitPrice == null || l.unitPrice < 0)) {
      toast.error("Enter a unit price for every line");
      return;
    }
    setSubmitting(true);
    try {
      await submitPublicQuote(token, { overallNotes: overallNotes || null, lines });
      setDone("submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  const submitService = async () => {
    if (serviceTotal === "" || Number(serviceTotal) < 0) {
      toast.error("Enter your total price");
      return;
    }
    setSubmitting(true);
    try {
      await submitPublicQuote(token, {
        totalAmount: Number(serviceTotal),
        leadTimeDays: serviceLeadTime === "" ? null : Number(serviceLeadTime),
        overallNotes: overallNotes || null,
      });
      setDone("submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      await declinePublicTender(token);
      setDone("declined");
    } catch (e) {
      toast.error("Failed to decline");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Centered>Loading…</Centered>;
  if (error) return <Centered><p className="text-rose-600">{error}</p></Centered>;
  if (done === "submitted")
    return <Centered><Success title="Quote submitted" body="Thank you — your quote has been received. You may reopen this link to update it before the deadline." /></Centered>;
  if (done === "declined")
    return <Centered><Success title="Tender declined" body="You have declined this tender. Thank you for letting us know." /></Centered>;

  const isService = form.tenderType === "SERVICE";
  const mi = form.moveIn || {};
  const cityLine = (city, state, pin) => [city, state, pin].filter(Boolean).join(", ") || "—";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
          <p className="text-xs font-semibold text-flamingo-600 uppercase tracking-wide">Request for Quote</p>
          <h1 className="text-xl font-bold text-slate-900 mt-1">{form.title || form.packageLabel || "Tender"}</h1>
          <div className="mt-2 text-sm text-slate-500 space-y-0.5">
            {form.vendorName && <p>Vendor: <span className="text-slate-700 font-medium">{form.vendorName}</span></p>}
            {form.packageLabel && <p>Package: {form.packageLabel}</p>}
            {fmtDate(form.responseDeadline) && <p>Respond by: {fmtDate(form.responseDeadline)}</p>}
          </div>
        </div>

        {isService ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            {/* Move-in details (read-only) */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 mb-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center rounded-lg bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">BHK: {mi.bhk || "—"}</span>
                <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-semibold ${mi.isIntercity ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                  {mi.isIntercity ? "Intercity move" : "Local move"}
                </span>
                {(fmtDate(mi.moveInDateStart) || fmtDate(mi.moveInDateEnd)) && (
                  <span className="inline-flex items-center rounded-lg bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-600">
                    Window: {fmtDate(mi.moveInDateStart) || "—"} → {fmtDate(mi.moveInDateEnd) || "—"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">From</p>
                  <p className="text-slate-800">{cityLine(mi.sourceCity, mi.sourceState, mi.sourcePincode)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">To</p>
                  <p className="text-slate-800">{cityLine(mi.destinationCity, mi.destinationState, mi.destinationPincode)}</p>
                </div>
              </div>
              {mi.notes && <p className="mt-3 text-xs text-slate-500">Notes: {mi.notes}</p>}
            </div>

            <p className="text-sm text-slate-600 mb-4">Quote your all-inclusive price for this move.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Your total price (₹)">
                <input type="number" min="0" step="0.01" value={serviceTotal}
                  onChange={(e) => setServiceTotal(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
              </Field>
              <Field label="Lead time (days)">
                <input type="number" min="0" value={serviceLeadTime}
                  onChange={(e) => setServiceLeadTime(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
              </Field>
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
              <textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)} rows={2}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
            </div>
            <div className="mt-5 flex items-center justify-between">
              <button onClick={decline} disabled={submitting} className="text-sm text-slate-500 hover:text-rose-600">Decline tender</button>
              <button onClick={submitService} disabled={submitting}
                className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5">
                {submitting ? "Submitting…" : "Submit Quote"}
              </button>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <p className="text-sm text-slate-600 mb-4">
            Quote your best unit price and lead time for each specified item below.
          </p>
          <div className="space-y-4">
            {(form.lines || []).map((l) => {
              const r = rows[l.interiorDesignerBoqId] || {};
              return (
                <div key={l.interiorDesignerBoqId} className="border border-slate-200 rounded-xl p-4">
                  <div className="mb-3">
                    <p className="text-sm font-semibold text-slate-900">{l.skuText || l.material}</p>
                    <p className="text-xs text-slate-500">
                      {[l.brandText, l.material, l.materialSubcategory, l.room, l.workItem]
                        .filter(Boolean)
                        .join(" · ")}
                      {l.quantity != null ? ` · Qty ${l.quantity}` : ""}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label="Unit price (₹)">
                      <input type="number" min="0" step="0.01" value={r.unitPrice}
                        onChange={(e) => setRow(l.interiorDesignerBoqId, "unitPrice", e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
                    </Field>
                    <Field label="Lead time (days)">
                      <input type="number" min="0" value={r.leadTimeDays}
                        onChange={(e) => setRow(l.interiorDesignerBoqId, "leadTimeDays", e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
                    </Field>
                    <Field label="Availability">
                      <select value={r.availability}
                        onChange={(e) => setRow(l.interiorDesignerBoqId, "availability", e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500">
                        <option value="">—</option>
                        {AVAILABILITY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
            <textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)} rows={2}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500" />
          </div>

          <div className="mt-5 flex items-center justify-between">
            <button onClick={decline} disabled={submitting} className="text-sm text-slate-500 hover:text-rose-600">
              Decline tender
            </button>
            <button onClick={submit} disabled={submitting}
              className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5">
              {submitting ? "Submitting…" : "Submit Quote"}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md text-center">{children}</div>
  </div>
);

const Success = ({ title, body }) => (
  <>
    <h1 className="text-lg font-bold text-slate-900">{title}</h1>
    <p className="text-sm text-slate-500 mt-2">{body}</p>
  </>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default TenderQuotePage;
